import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { parseZonedDateTime } from '@internationalized/date';
import { format } from 'date-fns';
import { useCreateBenchmarkRoute } from '@/queries/mission-planner';
import { CaseFacility, DonorType, OrganType } from '@/api/apis/case';
import { BenchmarkData } from '@/api/apis/route';
import { useSetBenchmark } from '@/queries/route-planner';
import { checkBenchmarkStatus } from '@/queries/mission-planner/mission-planner.fetcher';
import { retrieveUnifiedError } from '@/api/utils';

type UseSetBenchmarkToCaseProps = {
  caseId: string;
  donorHospital: CaseFacility;
  txCenter?: CaseFacility;
  donorType: DonorType;
  organType: OrganType;
  donorHospitalTime: string | null;
  donorHospitalDate: string | null;
  donorHospitalTimeZone: string | null;
};

export const useSetBenchmarkToCase = ({
  caseId,
  donorHospital,
  txCenter,
  donorType,
  organType,
  donorHospitalTime,
  donorHospitalDate,
  donorHospitalTimeZone,
}: UseSetBenchmarkToCaseProps) => {
  const { mutateAsync: getTaskId } = useCreateBenchmarkRoute();
  const { mutateAsync: setBenchmark } = useSetBenchmark();

  return useCallback(async (): Promise<BenchmarkData | null> => {
    try {
      if (!txCenter) return null;

      const formatted = format(new Date(`${donorHospitalDate} ${donorHospitalTime}`), `yyyy-MM-dd'T'HH:mm:ss`);
      const orTime = parseZonedDateTime(`${formatted}[${donorHospitalTimeZone}]`).toAbsoluteString();
      const orStringWithoutMs = orTime.match(/[^.]*/)?.[0];
      const orTimeShortUtcString = `${orStringWithoutMs}Z`;

      const { taskId } = await getTaskId({
        hospitalId: donorHospital.id,
        txCenterId: txCenter.id,
        caseType: donorType,
        organType,
        orTime: orTimeShortUtcString,
        caseId,
      });

      const completedBenchmarkId = await checkBenchmarkStatus(taskId);

      return await setBenchmark({ caseId, taskId: completedBenchmarkId });
    } catch (e) {
      toast.error(retrieveUnifiedError(e, 'Error setting benchmark to case').message);
      return null;
    }
  }, [
    caseId,
    donorHospital,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    donorType,
    getTaskId,
    setBenchmark,
    organType,
    txCenter,
  ]);
};
